import React from 'react';

type MySubscriptionsSkeletonProps = {
  variant?: 'desktop' | 'mobile';
  count?: number;
};

/** Placeholder card matching SubscriptionCard layout while subscriptions load */
const SubscriptionCardSkeleton: React.FC<{ variant: 'desktop' | 'mobile' }> = ({
  variant,
}) => {
  const isDesktop = variant === 'desktop';

  return (
    <div className="border border-[#E9E9E9] rounded-xl overflow-hidden bg-white relative">
      <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none" />
      <div
        className={`flex ${
          isDesktop ? 'flex-col md:flex-row md:items-center' : 'flex-col'
        } gap-4 p-4`}
      >
        <div
          className={`${
            isDesktop ? 'w-16 h-16 mr-2' : 'w-12 h-12'
          } rounded-lg bg-gray-200/70 border border-[#E9E9E9]/70 animate-pulse flex-shrink-0`}
        />
        <div className="flex flex-col flex-1 gap-2 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="h-5 bg-gray-200/70 rounded w-40 animate-pulse" />
            <div className="h-5 bg-gray-200/70 rounded w-16 animate-pulse" />
          </div>
          <div className="h-3 bg-gray-200/70 rounded w-full animate-pulse" />
          <div className="h-3 bg-gray-200/70 rounded w-3/4 animate-pulse" />
          <div className="flex flex-wrap gap-2 mt-1">
            <div className="h-8 bg-gray-200/70 rounded-md w-36 animate-pulse" />
            <div className="h-8 bg-gray-200/70 rounded-md w-40 animate-pulse" />
          </div>
          <div className="h-3 bg-gray-200/70 rounded w-48 animate-pulse mt-1" />
        </div>
        {isDesktop ? (
          <div className="h-10 bg-gray-200/70 rounded-md w-36 animate-pulse flex-shrink-0" />
        ) : (
          <div className="h-10 bg-gray-200/70 rounded-md w-full animate-pulse" />
        )}
      </div>
    </div>
  );
};

const MySubscriptionsSkeleton: React.FC<MySubscriptionsSkeletonProps> = ({
  variant = 'desktop',
  count = 2,
}) => {
  const isDesktop = variant === 'desktop';

  return (
    <div className="my-5 w-full" role="status" aria-label="Loading subscriptions">
      {isDesktop ? (
        <div className="h-4 bg-gray-200/70 rounded w-56 mb-6 animate-pulse" />
      ) : (
        <div className="h-4 bg-gray-200/70 rounded w-48 mb-4 animate-pulse" />
      )}
      <div className="flex flex-col gap-4">
        {Array.from({ length: count }).map((_, index) => (
          <SubscriptionCardSkeleton key={index} variant={variant} />
        ))}
      </div>
      <span className="sr-only">Loading subscriptions...</span>
    </div>
  );
};

export default MySubscriptionsSkeleton;
